import React from 'react';
import { DialogTitle, Box, Typography, IconButton } from '@mui/material';
import {
  Close as CloseIcon,
  Science as ExperimentIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ExperimentStatusActions from './ExperimentStatusActions';

/**
 * ExperimentDialogHeader Component
 *
 * Displays the dialog header with experiment name, icon, status actions, and close button.
 *
 * @param {Object} props
 * @param {Object} props.experiment - Experiment object
 * @param {Function} props.onClose - Callback to close dialog
 * @param {Function} props.onStatusChange - Callback when status changes
 */
function ExperimentDialogHeader({ experiment, onClose, onStatusChange }) {
  const theme = useTheme();

  return (
    <DialogTitle
      sx={{
        pb: 1.5,
        pt: 2.5,
        pl: 3,
        pr: 1, // Minimal right padding since X button will be positioned absolutely
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        position: 'relative', // For absolute positioning of X button
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: theme.palette.action.hover,
            }}
          >
            <ExperimentIcon
              sx={{
                fontSize: '18px',
                color: theme.palette.primary.main,
              }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.0625rem',
              fontWeight: 600,
              color: theme.palette.text.primary,
              letterSpacing: '-0.01em',
            }}
          >
            {experiment?.name || 'Experiment Details'}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5} sx={{ pr: 12 }}>
          <ExperimentStatusActions
            experiment={experiment}
            onStatusChange={onStatusChange}
          />
        </Box>
      </Box>
      {/* X button positioned at absolute right with padding */}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: 'absolute',
          right: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
  );
}

export default ExperimentDialogHeader;
